import { useState, useEffect, useMemo, useRef } from 'react';
import countries from 'i18n-iso-countries';
import 'flag-icons/css/flag-icons.min.css';
import { useLanguage } from '@/contexts/LanguageContext';

// Register all 7 supported locales (matches LanguageContext).
import enLocale from 'i18n-iso-countries/langs/en.json';
import ruLocale from 'i18n-iso-countries/langs/ru.json';
import esLocale from 'i18n-iso-countries/langs/es.json';
import deLocale from 'i18n-iso-countries/langs/de.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';
import ptLocale from 'i18n-iso-countries/langs/pt.json';
import zhLocale from 'i18n-iso-countries/langs/zh.json';

[enLocale, ruLocale, esLocale, deLocale, frLocale, ptLocale, zhLocale].forEach((l) => {
  try { countries.registerLocale(l); } catch (_) {}
});

const SUPPORTED = ['en', 'ru', 'es', 'de', 'fr', 'pt', 'zh'];

/**
 * CountrySelector — combobox: text input + dropdown list with flag + localized name.
 * Optional field; supports clear. Pure client-side (no network).
 *
 * Props:
 *   value: ISO 3166-1 alpha-2 code (e.g. "RU") or "" / null
 *   onChange: (code: string | null) => void
 *   placeholder?: string
 *   disabled?: boolean
 */
const CountrySelector = ({ value, onChange, placeholder, disabled = false }) => {
  const { language, t } = useLanguage();
  const locale = SUPPORTED.includes(language) ? language : 'en';

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Full list (memoized per locale)
  const allCountries = useMemo(() => {
    const names = countries.getNames(locale, { select: 'official' });
    return Object.entries(names)
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name, locale));
  }, [locale]);

  // Find current selection label
  const selectedName = useMemo(() => {
    if (!value) return '';
    return countries.getName(value, locale) || countries.getName(value, 'en') || value;
  }, [value, locale]);

  // When `value` changes externally, sync input text (only when closed)
  useEffect(() => {
    if (!open) setQuery(selectedName);
  }, [selectedName, open]);

  // Filter as user types
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    if (!q || q === selectedName.toLowerCase()) return allCountries.slice(0, 50);
    // Match by localized name OR by ISO code prefix
    return allCountries
      .filter((c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().startsWith(q))
      .slice(0, 50);
  }, [query, selectedName, allCountries]);

  // Close on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const handleSelect = (code) => {
    onChange?.(code || null);
    setOpen(false);
    setHighlight(0);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setQuery('');
    onChange?.(null);
    setOpen(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (['ArrowDown', 'Enter'].includes(e.key)) {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[highlight];
      if (pick) handleSelect(pick.code);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const inputPlaceholder = placeholder ?? t('auth.countryPlaceholder', {}) ?? 'Search country…';

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%', zIndex: open ? 1101 : 'auto' }}
      data-testid="country-selector"
    >
      <div style={{ position: 'relative' }}>
        {value && (
          <span
            className={`fi fi-${String(value).toLowerCase()}`}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 22,
              height: 16,
              borderRadius: 2,
              boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
            }}
          />
        )}
        <input
          ref={inputRef}
          type="text"
          value={open ? query : selectedName}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setHighlight(0); }}
          onFocus={() => { setOpen(true); setQuery(''); setHighlight(0); }}
          onKeyDown={handleKeyDown}
          placeholder={inputPlaceholder}
          disabled={disabled}
          data-testid="country-selector-input"
          style={{
            width: '100%',
            paddingLeft: value ? '2.5rem' : '0.75rem',
            paddingRight: value ? '2rem' : '0.75rem',
            paddingTop: '0.625rem',
            paddingBottom: '0.625rem',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            fontSize: '1rem',
            background: '#fff',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear"
            data-testid="country-selector-clear"
            style={{
              position: 'absolute',
              right: '0.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: '#9ca3af',
              fontSize: '1.1rem',
              lineHeight: 1,
              padding: '0.25rem',
            }}
          >
            ×
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <ul
          role="listbox"
          data-testid="country-selector-list"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            margin: 0,
            padding: '0.25rem 0',
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 1100,
            listStyle: 'none',
          }}
        >
          {filtered.map((c, idx) => (
            <li
              key={c.code}
              role="option"
              aria-selected={value === c.code}
              data-testid={`country-option-${c.code}`}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(c.code); }}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.625rem',
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                background: highlight === idx ? '#f3f4f6' : 'transparent',
                fontSize: '0.95rem',
              }}
            >
              <span
                className={`fi fi-${c.code.toLowerCase()}`}
                aria-hidden="true"
                style={{ width: 22, height: 16, borderRadius: 2, flexShrink: 0, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }}
              />
              <span style={{ flex: 1 }}>{c.name}</span>
              <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{c.code}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CountrySelector;
